import { useOrganisationColumns } from './columns';
import { DataTable } from '@/components/ui/table/data-table';
import OrgTableAction from './organisation-table-action';
import { ProfilePanel } from '@/modules/profile/profile-sheet';
import { useState, useMemo, useCallback, memo } from 'react';
import { Icons } from '@/components/icons';
import type { Organisation } from '@/services/types';

interface AllOrganizationTableProps {
  data: Organisation[];
  totalData: number;
}

function AllOrganisationTable({
  data,
  totalData
}: AllOrganizationTableProps) {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [selectedOrganisation, setSelectedOrganisation] = useState<Organisation | null>(null);

  const { columns, checkedRowIds, setCheckedRowIds } = useOrganisationColumns();

  const handleRowClick = useCallback((organisation: Organisation) => {
    setSelectedOrganisation(organisation);
    setIsPanelOpen(true);
  }, []);

  // Memoize the DataTable props to prevent unnecessary re-renders
  const dataTableProps = useMemo(() => ({
    columns,
    data,
    totalItems: totalData,
    emptyState: (
      <div className='flex h-full w-full flex-col items-center justify-center'>
        <Icons.emptyState />
        No organisations found
      </div>
    ),
    onRowClick: handleRowClick
  }), [columns, data, totalData, handleRowClick]);

  const handleClosePanel = useCallback((open: boolean) => {
    setIsPanelOpen(open);
    if (!open) {
      // Small delay to prevent immediate re-opening
      setTimeout(() => {
        setSelectedOrganisation(null);
      }, 100);
    }
  }, []);

  return (
    <div className='space-y-4 bg-white'>
      <OrgTableAction data={data} checkedRows={checkedRowIds} setCheckedRows={setCheckedRowIds} />
      <div className='px-4'>
        <DataTable {...dataTableProps} />
      </div>

      {/* Only render ProfilePanel when we have a selected organisation */}
      {selectedOrganisation && (
        <ProfilePanel
          key={selectedOrganisation.id} // Key to prevent unmounting issues
          data={selectedOrganisation}
          open={isPanelOpen}
          onOpenChange={handleClosePanel}
        />
      )}
    </div>
  );
}

export default memo(AllOrganisationTable);
